import { supabase } from '@/integrations/supabase/client';
import { readCookie, isBotOrPrerender } from '@/lib/oaiqCapi';
import { track } from '@/lib/track';
import { hasMarketingConsent } from '@/lib/consent';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`;

/**
 * Auth is mandatory: identity and price are resolved server-side from the
 * bearer token and the ebook record — never sent from the client.
 */
async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('AUTH_REQUIRED');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function createCheckoutSession(params: {
  ebook_id: string;
  ebook_slug: string;
}) {
  const adConsent = hasMarketingConsent();
  const oppref = adConsent ? readCookie('__oppref') : undefined;
  const obref = adConsent ? readCookie('__obref') : undefined;
  const res = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      action: 'create',
      ...params,
      ad_consent: adConsent,
      ...(oppref ? { oppref } : {}),
      ...(obref ? { obref } : {}),
    }),
  });
  if (!res.ok) throw new Error('Failed to create checkout session');
  return res.json() as Promise<{ url: string; session_id: string }>;
}

export interface OaiqOrderInfo {
  event_id: string;
  amount: number; // satang
  currency: string;
  ebook_id: string;
  slug?: string;
}

/**
 * Fire the OpenAI Pixel `order_created` once per event_id (dedupes against the
 * server-side CAPI copy via the shared event_id). Never throws — a tracking
 * failure must not affect the unlock UI.
 */
function trackOrderCreated(oaiq: OaiqOrderInfo) {
  try {
    if (isBotOrPrerender()) return;
    const dedupeKey = `aim_oaiq_order_${oaiq.event_id}`;
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, '1');

    if (hasMarketingConsent()) (window as any).oaiq?.(
      'measure',
      'order_created',
      {
        type: 'contents',
        amount: oaiq.amount,
        currency: oaiq.currency,
        contents: [
          {
            id: oaiq.ebook_id,
            name: oaiq.slug ?? 'ebook',
            content_type: 'product',
            quantity: 1,
            amount: oaiq.amount,
            currency: oaiq.currency,
          },
        ],
      },
      { event_id: oaiq.event_id }
    );

    track('purchase', 'books', {
      target: oaiq.slug,
      value: oaiq.amount / 100,
      meta: { event_id: oaiq.event_id, currency: oaiq.currency },
    });
  } catch {
    // never affect the purchase flow
  }
}

export async function verifyCheckoutSession(sessionId: string) {
  const res = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ action: 'verify', session_id: sessionId }),
  });
  if (!res.ok) throw new Error('Failed to verify session');
  const json = (await res.json()) as {
    status: string;
    claimed: boolean;
    error?: string;
    oaiq?: OaiqOrderInfo;
  };
  if (json.status === 'paid' && json.oaiq?.event_id) {
    trackOrderCreated(json.oaiq);
  }
  return json;
}
